import YAML from 'yaml';
import {RegisterWizardFormValues} from './types';

/**
 * Derives a catalog component name from a repo URL, e.g.
 * https://bitbucket.org/parivedasolutions/my-repo -> my-repo
 */
export function slugFromRepoUrl(repoUrl: string): string {
  try {
    const {pathname} = new URL(repoUrl);
    const segments = pathname
      .replace(/\.git$/, '')
      .split('/')
      .filter(Boolean);
    return (segments[segments.length - 1] ?? '').toLowerCase();
  } catch {
    return '';
  }
}

export function parseTags(tags: string): string[] {
  return tags
    .split(',')
    .map(tag => tag.trim().toLowerCase())
    .filter(Boolean);
}

/**
 * Builds the in-memory catalog-info.yaml content for a component being
 * registered through the wizard. spec.owner intentionally stays
 * 'parivedasolutions' per the existing taxonomy convention (see
 * components/templating/CodeArea.tsx); the logged-in user is recorded
 * separately via the pariveda.io/registered-by annotation.
 */
export function generateCatalogInfoYaml(
  values: RegisterWizardFormValues,
  registeredBy: string,
): string {
  const name = slugFromRepoUrl(values.repoUrl);
  const tags = parseTags(values.tags);

  const links = [
    values.website ? {title: 'Website', url: values.website} : undefined,
    values.slackChannelId
      ? {
          title: 'Slack Channel',
          url: `https://pariveda.slack.com/archives/${values.slackChannelId}`,
        }
      : undefined,
  ].filter((link): link is {title: string; url: string} => Boolean(link));

  const metadata: Record<string, unknown> = {
    name,
    description: values.description,
  };
  if (links.length) {
    metadata.links = links;
  }
  metadata.annotations = {
    'backstage.io/techdocs-ref': 'dir:.',
    'pariveda.io/registered-by': registeredBy,
  };
  if (tags.length) {
    metadata.tags = tags;
  }

  const catalogInfo = {
    apiVersion: 'backstage.io/v1alpha1',
    kind: 'Component',
    metadata,
    spec: {
      type: values.type,
      owner: 'parivedasolutions',
      lifecycle: values.lifecycle,
      system: values.system,
    },
  };

  return YAML.stringify(catalogInfo, {lineWidth: 0});
}
